import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import {
  ArrowRight, Phone, CheckCircle2, Activity, Bone, Stethoscope,
  Footprints, Zap, ChevronRight, MapPin, Clock, Shield, Award,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { conditionsData } from '../data/conditionsData';

const iconMap = {
  Activity,
  Bone,
  Stethoscope,
  Footprints,
  Zap,
};

const ConditionDetail = () => {
  const { slug } = useParams();
  const condition = conditionsData[slug];

  if (!condition) {
    return <Navigate to="/conditions" replace />;
  }

  const Icon = iconMap[condition.icon] || Activity;

  // Other conditions for "Related Conditions" section (exclude current)
  const relatedConditions = Object.entries(conditionsData)
    .filter(([s]) => s !== slug)
    .slice(0, 6);

  // MedicalCondition Schema markup
  const conditionSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": condition.name,
    "description": condition.overview,
    "associatedAnatomy": {
      "@type": "AnatomicalStructure",
      "name": "Musculoskeletal system",
    },
    "possibleTreatment": {
      "@type": "MedicalTherapy",
      "name": "Physiotherapy",
      "provider": {
        "@type": "MedicalBusiness",
        "name": "MSK and Sports Physio",
        "telephone": "+447747075809",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "HarroWall Climbing Centre, Neptune Trading Estate, Neptune Rd",
          "addressLocality": "Harrow",
          "postalCode": "HA1 4HX",
          "addressCountry": "GB",
        },
      },
    },
  };

  // FAQPage Schema for rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": condition.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={condition.title}
        description={condition.metaDescription}
        keywords={condition.keywords}
        type="website"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(conditionSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="pt-24 pb-2 px-4 lg:px-8 bg-gray-50" aria-label="Breadcrumb">
        <div className="container mx-auto max-w-6xl">
          <ol className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <li>
              <Link to="/" className="hover:text-blue-600">Home</Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li>
              <Link to="/conditions" className="hover:text-blue-600">Conditions</Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li className="text-gray-900 font-medium">{condition.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-10 md:py-16 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-start gap-4 md:gap-6 mb-6">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-7 h-7 md:w-8 md:h-8 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-1">
                Conditions We Treat
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {condition.name} Physiotherapy in Harrow
              </h1>
            </div>
          </div>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8 max-w-3xl">
            {condition.heroLine}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-book-btn"
              onClick={() => window.location.href = '/contact'}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-5 text-base font-semibold rounded-lg shadow-lg"
            >
              Book Your Assessment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:07747075809'}
              size="lg"
              variant="outline"
              className="px-6 py-5 text-base font-semibold rounded-lg border-2 border-gray-300 hover:border-blue-600"
            >
              <Phone className="mr-2 w-5 h-5" />
              07747075809
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Understanding {condition.name}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-5 text-base md:text-lg">
              {condition.overview}
            </p>
            <p className="text-gray-700 leading-relaxed mb-10 text-base md:text-lg">
              {condition.secondPara}
            </p>
          </div>

          {/* Symptoms & Causes Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="border-2 border-gray-100">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-red-600 font-bold text-sm">!</span>
                  </span>
                  Common Symptoms
                </h3>
                <ul className="space-y-3">
                  {condition.symptoms.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 font-bold text-sm">?</span>
                  </span>
                  Common Causes
                </h3>
                <ul className="space-y-3">
                  {condition.causes.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Treatment Approach */}
          <Card className="border-2 border-blue-100 bg-blue-50/30 mb-12">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Our Treatment Approach for {condition.name}
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Every treatment plan is tailored to your specific condition, lifestyle and goals. Here&apos;s how we approach {condition.name.toLowerCase()}:
              </p>
              <ul className="space-y-3 mb-6">
                {condition.treatment.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-800">
                    <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-blue-200 pt-5 mt-5">
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Expected Timeline</h4>
                    <p className="text-gray-700">{condition.timeline}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Choose Us */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Why Choose MSK and Sports Physio for {condition.name}?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">HCPC Registered & CSP Member</h4>
                  <p className="text-sm text-gray-600">Fully qualified, regulated and insured.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Award className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">MSc Sports Medicine</h4>
                  <p className="text-sm text-gray-600">Advanced postgraduate qualification.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Same-Day Appointments</h4>
                  <p className="text-sm text-gray-600">Get assessed and treated quickly.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Central Harrow Location</h4>
                  <p className="text-sm text-gray-600">HarroWall Climbing Centre, HA1 4HX.</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {condition.faqs.map((faq, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-blue-200 transition-colors">
                  <CardContent className="p-5 md:p-6">
                    <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">
                      {faq.q}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                      {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 px-4 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Treat Your {condition.name}?
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Book your initial assessment today and take the first step towards regaining strength and restoring movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.href = '/contact'}
              size="lg"
              className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-6 text-base font-semibold rounded-lg shadow-lg"
            >
              Book Your Assessment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:07747075809'}
              size="lg"
              variant="outline"
              className="px-8 py-6 text-base font-semibold rounded-lg border-2 border-white text-white hover:bg-white hover:text-blue-600 bg-transparent"
            >
              <Phone className="mr-2 w-5 h-5" />
              07747075809
            </Button>
          </div>
        </div>
      </section>

      {/* Related Conditions */}
      <section className="py-12 md:py-16 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
            Other Conditions We Treat
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedConditions.map(([s, c]) => {
              const RelIcon = iconMap[c.icon] || Activity;
              return (
                <Link
                  key={s}
                  to={`/conditions/${s}`}
                  className="group block"
                >
                  <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all duration-300 h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                          <RelIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {c.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {c.heroLine.replace(' in Harrow', '')}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/conditions"
              className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
            >
              View All Conditions
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConditionDetail;
